import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator } from '@/modules/wms/components/common/StatusIndicator';
import { devicesAPI } from '@/shared/services/api';
import {
  Plus,
  Printer,
  ScanLine,
  Smartphone,
  Wifi,
  WifiOff,
  Settings,
  Trash2,
  Edit,
  RefreshCw,
  CheckCircle2,
  XCircle,
  MoreHorizontal,
  Loader2,
  Usb,
  Bluetooth,
  Camera,
  TestTube,
  AlertTriangle,
  MapPin,
  Thermometer,
  Cpu } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription } from
'@/shared/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator } from
'@/shared/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { toast } from 'sonner';

const deviceTypeIcons = {
  barcode_scanner: ScanLine,
  label_printer: Printer,
  mobile_device: Smartphone,
  gps_tracker: MapPin,
  temperature_sensor: Thermometer
};

const deviceTypeLabels = {
  barcode_scanner: 'Barcode Scanner',
  label_printer: 'Label Printer',
  mobile_device: 'Mobile Device',
  gps_tracker: 'GPS Tracker',
  temperature_sensor: 'Temperature Sensor'
};

const deviceTypeColors = {
  barcode_scanner: { bg: 'bg-blue-100', text: 'text-blue-600', dark: 'dark:bg-blue-900/50', border: 'border-l-blue-500' },
  label_printer: { bg: 'bg-purple-100', text: 'text-purple-600', dark: 'dark:bg-purple-900/50', border: 'border-l-purple-500' },
  mobile_device: { bg: 'bg-green-100', text: 'text-green-600', dark: 'dark:bg-green-900/50', border: 'border-l-green-500' },
  gps_tracker: { bg: 'bg-amber-100', text: 'text-amber-600', dark: 'dark:bg-amber-900/50', border: 'border-l-amber-500' },
  temperature_sensor: { bg: 'bg-cyan-100', text: 'text-cyan-600', dark: 'dark:bg-cyan-900/50', border: 'border-l-cyan-500' }
};

const connectionTypeIcons = {
  usb: Usb,
  network: Wifi,
  bluetooth: Bluetooth,
  mobile_camera: Camera
};

const statusColors = {
  online: 'bg-green-500',
  offline: 'bg-gray-400',
  error: 'bg-red-500',
  configuring: 'bg-amber-500'
};

export const DeviceManagement = () => {const { t } = useTranslation("wms");
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({
    total_devices: 0,
    scanners: { total: 0, online: 0 },
    printers: { total: 0, online: 0 },
    mobile_devices: { total: 0, online: 0 },
    gps_trackers: { total: 0, online: 0 },
    temperature_sensors: { total: 0, online: 0 }
  });
  const [deviceModels, setDeviceModels] = useState({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [testingDevice, setTestingDevice] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [newDevice, setNewDevice] = useState({
    name: '',
    device_type: '',
    connection_type: '',
    model: '',
    ip_address: '',
    port: '',
    location: '',
    serial_number: '',
    label_width: 102,
    label_height: 152,
    dpi: 203,
    scan_mode: 'continuous',
    beep_on_scan: true
  });

  // Determine active tab from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/scanners')) setActiveTab('scanners');else
    if (path.includes('/printers')) setActiveTab('printers');else
    if (path.includes('/mobile')) setActiveTab('mobile');else
    if (path.includes('/gps')) setActiveTab('gps');else
    if (path.includes('/sensors')) setActiveTab('sensors');else
    setActiveTab('all');
  }, [location.pathname]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [devicesData, statsData, modelsData] = await Promise.all([
      devicesAPI.getDevices(),
      devicesAPI.getStats(),
      devicesAPI.getModels()]
      );
      setDevices(devicesData || []);
      setStats(statsData || {});
      setDeviceModels(modelsData || {});
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = async () => {
    try {
      const deviceData = {
        ...newDevice,
        port: newDevice.port ? parseInt(newDevice.port) : null,
        label_width: newDevice.device_type === 'label_printer' ? parseInt(newDevice.label_width) : null,
        label_height: newDevice.device_type === 'label_printer' ? parseInt(newDevice.label_height) : null,
        dpi: newDevice.device_type === 'label_printer' ? parseInt(newDevice.dpi) : null
      };

      await devicesAPI.createDevice(deviceData);
      toast.success('Device added successfully');
      setShowAddDialog(false);
      resetNewDevice();
      fetchData();
    } catch (error) {
      console.error('Failed to add device:', error);
      toast.error('Failed to add device');
    }
  };

  const handleEditDevice = async () => {
    if (!selectedDevice) return;
    try {
      await devicesAPI.updateDevice(selectedDevice.id, {
        name: selectedDevice.name,
        location: selectedDevice.location,
        ip_address: selectedDevice.ip_address,
        port: selectedDevice.port ? parseInt(selectedDevice.port) : null,
        is_active: selectedDevice.is_active
      });
      toast.success('Device updated successfully');
      setShowEditDialog(false);
      setSelectedDevice(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update device:', error);
      toast.error('Failed to update device');
    }
  };

  const handleDeleteDevice = async () => {
    if (!selectedDevice) return;
    try {
      await devicesAPI.deleteDevice(selectedDevice.id);
      toast.success('Device deleted');
      setShowDeleteDialog(false);
      setSelectedDevice(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete device:', error);
      toast.error('Failed to delete device');
    }
  };

  const handleTestConnection = async (device) => {
    setTestingDevice(device.id);
    try {
      const result = await devicesAPI.testConnection(device.id);
      if (result.success) {
        toast.success('Connection successful', { description: `Response time: ${result.response_time_ms}ms` });
      } else {
        toast.error('Connection failed', { description: result.error });
      }
      fetchData();
    } catch (error) {
      console.error('Test failed:', error);
      toast.error('Connection test failed');
    } finally {
      setTestingDevice(null);
    }
  };

  const resetNewDevice = () => {
    setNewDevice({
      name: '',
      device_type: '',
      connection_type: '',
      model: '',
      ip_address: '',
      port: '',
      location: '',
      serial_number: '',
      label_width: 102,
      label_height: 152,
      dpi: 203,
      scan_mode: 'continuous',
      beep_on_scan: true
    });
  };

  const getAvailableConnectionTypes = (deviceType) => {
    const models = deviceModels[deviceType] || [];
    const types = new Set();
    models.forEach((m) => m.connection_types?.forEach((t) => types.add(t)));
    return Array.from(types);
  };

  const filterDevicesByType = (type) => {
    if (type === 'all') return devices;
    if (type === 'scanners') return devices.filter((d) => d.device_type === 'barcode_scanner');
    if (type === 'printers') return devices.filter((d) => d.device_type === 'label_printer');
    if (type === 'mobile') return devices.filter((d) => d.device_type === 'mobile_device');
    if (type === 'gps') return devices.filter((d) => d.device_type === 'gps_tracker');
    if (type === 'sensors') return devices.filter((d) => d.device_type === 'temperature_sensor');
    return devices.filter((d) => d.device_type === type);
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case 'scanners':return 'Barcode Scanners';
      case 'printers':return 'Label Printers';
      case 'mobile':return 'Mobile Devices';
      case 'gps':return 'GPS Trackers';
      case 'sensors':return 'Temperature Sensors';
      default:return 'Device Management';
    }
  };

  const getPageDescription = () => {
    switch (activeTab) {
      case 'scanners':return 'Configure handheld and fixed barcode scanners';
      case 'printers':return 'Manage label and shipping printers';
      case 'mobile':return 'Register Android smartphones and tablets for scanning';
      case 'gps':return 'Track vehicles and assets with GPS devices';
      case 'sensors':return 'Monitor cold chain and temperature-controlled storage';
      default:return 'Configure barcode scanners, label printers, GPS trackers, and sensors';
    }
  };

  return (
    <div className="space-y-6" data-testid="device-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{getPageTitle()}</h1>
          <p className="text-muted-foreground mt-1">
            {getPageDescription()}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="add-device-btn">
            <Plus className="h-4 w-4" />{t("addDevice", "Add Device")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("total", "Total")}</p>
              <p className="text-2xl font-bold">{stats.total_devices}</p>
            </div>
            <Cpu className="h-8 w-8 text-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("scanners", "Scanners")}</p>
              <p className="text-2xl font-bold">{stats.scanners?.online || 0}<span className="text-sm text-muted-foreground">/{stats.scanners?.total || 0}</span></p>
            </div>
            <ScanLine className="h-8 w-8 text-blue-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-purple-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("printers", "Printers")}</p>
              <p className="text-2xl font-bold">{stats.printers?.online || 0}<span className="text-sm text-muted-foreground">/{stats.printers?.total || 0}</span></p>
            </div>
            <Printer className="h-8 w-8 text-purple-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("mobile", "Mobile")}</p>
              <p className="text-2xl font-bold">{stats.mobile_devices?.online || 0}<span className="text-sm text-muted-foreground">/{stats.mobile_devices?.total || 0}</span></p>
            </div>
            <Smartphone className="h-8 w-8 text-green-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("gps", "GPS")}</p>
              <p className="text-2xl font-bold">{stats.gps_trackers?.online || 0}<span className="text-sm text-muted-foreground">/{stats.gps_trackers?.total || 0}</span></p>
            </div>
            <MapPin className="h-8 w-8 text-amber-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-cyan-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("sensors", "Sensors")}</p>
              <p className="text-2xl font-bold">{stats.temperature_sensors?.online || 0}<span className="text-sm text-muted-foreground">/{stats.temperature_sensors?.total || 0}</span></p>
            </div>
            <Thermometer className="h-8 w-8 text-cyan-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Device Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="all" className="gap-2">{t("allDevices", "All Devices")}

            <Badge variant="secondary" className="ml-1">{devices.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="scanners" className="gap-2">
            <ScanLine className="h-4 w-4" />{t("scanners", "Scanners")}

          </TabsTrigger>
          <TabsTrigger value="printers" className="gap-2">
            <Printer className="h-4 w-4" />{t("printers", "Printers")}

          </TabsTrigger>
          <TabsTrigger value="mobile" className="gap-2">
            <Smartphone className="h-4 w-4" />{t("mobile", "Mobile")}

          </TabsTrigger>
          <TabsTrigger value="gps" className="gap-2">
            <MapPin className="h-4 w-4" />{t("gps", "GPS")}

          </TabsTrigger>
          <TabsTrigger value="sensors" className="gap-2">
            <Thermometer className="h-4 w-4" />{t("sensors", "Sensors")}

          </TabsTrigger>
        </TabsList>

        {/* All Devices Tab */}
        <TabsContent value="all">
          <DeviceGrid
            devices={filterDevicesByType('all')}
            loading={loading}
            testingDevice={testingDevice}
            onEdit={(d) => {setSelectedDevice(d);setShowEditDialog(true);}}
            onDelete={(d) => {setSelectedDevice(d);setShowDeleteDialog(true);}}
            onTest={handleTestConnection} />
          
        </TabsContent>

        {/* Scanners Tab */}
        <TabsContent value="scanners">
          <DeviceGrid
            devices={filterDevicesByType('scanners')}
            loading={loading}
            testingDevice={testingDevice}
            onEdit={(d) => {setSelectedDevice(d);setShowEditDialog(true);}}
            onDelete={(d) => {setSelectedDevice(d);setShowDeleteDialog(true);}}
            onTest={handleTestConnection}
            emptyMessage="No barcode scanners configured" />
          
        </TabsContent>

        {/* Printers Tab */}
        <TabsContent value="printers">
          <DeviceGrid
            devices={filterDevicesByType('printers')}
            loading={loading}
            testingDevice={testingDevice}
            onEdit={(d) => {setSelectedDevice(d);setShowEditDialog(true);}}
            onDelete={(d) => {setSelectedDevice(d);setShowDeleteDialog(true);}}
            onTest={handleTestConnection}
            emptyMessage="No label printers configured" />
          
        </TabsContent>

        {/* Mobile Tab */}
        <TabsContent value="mobile">
          <DeviceGrid
            devices={filterDevicesByType('mobile')}
            loading={loading}
            testingDevice={testingDevice}
            onEdit={(d) => {setSelectedDevice(d);setShowEditDialog(true);}}
            onDelete={(d) => {setSelectedDevice(d);setShowDeleteDialog(true);}}
            onTest={handleTestConnection}
            emptyMessage="No mobile devices registered" />
          
        </TabsContent>

        {/* GPS Tab */}
        <TabsContent value="gps">
          <DeviceGrid
            devices={filterDevicesByType('gps')}
            loading={loading}
            testingDevice={testingDevice}
            onEdit={(d) => {setSelectedDevice(d);setShowEditDialog(true);}}
            onDelete={(d) => {setSelectedDevice(d);setShowDeleteDialog(true);}}
            onTest={handleTestConnection}
            emptyMessage="No GPS trackers configured" />
          
        </TabsContent>

        {/* Sensors Tab */}
        <TabsContent value="sensors">
          <DeviceGrid
            devices={filterDevicesByType('sensors')}
            loading={loading}
            testingDevice={testingDevice}
            onEdit={(d) => {setSelectedDevice(d);setShowEditDialog(true);}}
            onDelete={(d) => {setSelectedDevice(d);setShowDeleteDialog(true);}}
            onTest={handleTestConnection}
            emptyMessage="No temperature sensors configured" />
          
        </TabsContent>
      </Tabs>

      {/* Add Device Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("addNewDevice", "Add New Device")}</DialogTitle>
            <DialogDescription>{t("configureANew", "Configure a new scanner, printer, or mobile device")}

            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("deviceName", "Device Name *")}</label>
              <Input
                placeholder="e.g., Warehouse Scanner A1"
                value={newDevice.name}
                onChange={(e) => setNewDevice((prev) => ({ ...prev, name: e.target.value }))}
                data-testid="device-name-input" />
              
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("deviceType", "Device Type *")}</label>
                <Select
                  value={newDevice.device_type}
                  onValueChange={(v) => setNewDevice((prev) => ({ ...prev, device_type: v, connection_type: '', model: '' }))}>
                  
                  <SelectTrigger data-testid="device-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="barcode_scanner">{t("barcodeScanner", "Barcode Scanner")}</SelectItem>
                    <SelectItem value="label_printer">{t("labelPrinter", "Label Printer")}</SelectItem>
                    <SelectItem value="mobile_device">{t("mobileDevice", "Mobile Device")}</SelectItem>
                    <SelectItem value="gps_tracker">{t("gpsTracker", "GPS Tracker")}</SelectItem>
                    <SelectItem value="temperature_sensor">{t("temperatureSensor", "Temperature Sensor")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("connectionType", "Connection Type *")}</label>
                <Select
                  value={newDevice.connection_type}
                  onValueChange={(v) => setNewDevice((prev) => ({ ...prev, connection_type: v }))}
                  disabled={!newDevice.device_type}>
                  
                  <SelectTrigger data-testid="connection-type-select">
                    <SelectValue placeholder="Select connection" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableConnectionTypes(newDevice.device_type).map((type) =>
                    <SelectItem key={type} value={type}>
                        {type === 'usb' && 'USB'}
                        {type === 'network' && 'Network (TCP/IP)'}
                        {type === 'bluetooth' && 'Bluetooth'}
                        {type === 'mobile_camera' && 'Mobile Camera'}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {newDevice.connection_type === 'network' &&
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("ipAddress", "IP Address *")}</label>
                  <Input
                  placeholder="192.168.1.100"
                  value={newDevice.ip_address}
                  onChange={(e) => setNewDevice((prev) => ({ ...prev, ip_address: e.target.value }))} />
                
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("port", "Port")}</label>
                  <Input
                  placeholder="9100"
                  value={newDevice.port}
                  onChange={(e) => setNewDevice((prev) => ({ ...prev, port: e.target.value }))} />
                
                </div>
              </div>
            }

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("model", "Model")}</label>
                <Select
                  value={newDevice.model}
                  onValueChange={(v) => setNewDevice((prev) => ({ ...prev, model: v }))}
                  disabled={!newDevice.device_type}>
                  
                  <SelectTrigger>
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {(deviceModels[newDevice.device_type] || []).map((m) =>
                    <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("location", "Location")}</label>
                <Input
                  placeholder="e.g., Zone A - Packing"
                  value={newDevice.location}
                  onChange={(e) => setNewDevice((prev) => ({ ...prev, location: e.target.value }))} />
                
              </div>
            </div>

            {newDevice.device_type === 'label_printer' &&
            <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("labelWidthMm", "Label Width (mm)")}</label>
                  <Input
                  type="number"
                  value={newDevice.label_width}
                  onChange={(e) => setNewDevice((prev) => ({ ...prev, label_width: e.target.value }))} />
                
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("labelHeightMm", "Label Height (mm)")}</label>
                  <Input
                  type="number"
                  value={newDevice.label_height}
                  onChange={(e) => setNewDevice((prev) => ({ ...prev, label_height: e.target.value }))} />
                
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("dpi", "DPI")}</label>
                  <Select
                  value={String(newDevice.dpi)}
                  onValueChange={(v) => setNewDevice((prev) => ({ ...prev, dpi: parseInt(v) }))}>
                  
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="203">{t("203Dpi", "203 DPI")}</SelectItem>
                      <SelectItem value="300">{t("300Dpi", "300 DPI")}</SelectItem>
                      <SelectItem value="600">{t("600Dpi", "600 DPI")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            }
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {setShowAddDialog(false);resetNewDevice();}}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleAddDevice}
              disabled={!newDevice.name || !newDevice.device_type || !newDevice.connection_type}
              data-testid="save-device-btn">{t("addDevice", "Add Device")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("editDevice", "Edit Device")}</DialogTitle>
          </DialogHeader>
          {selectedDevice &&
          <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("deviceName", "Device Name")}</label>
                <Input
                value={selectedDevice.name}
                onChange={(e) => setSelectedDevice((prev) => ({ ...prev, name: e.target.value }))} />
              
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("location", "Location")}</label>
                <Input
                value={selectedDevice.location || ''}
                onChange={(e) => setSelectedDevice((prev) => ({ ...prev, location: e.target.value }))} />
              
              </div>
              {selectedDevice.connection_type === 'network' &&
            <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t("ipAddress", "IP Address")}</label>
                    <Input
                  value={selectedDevice.ip_address || ''}
                  onChange={(e) => setSelectedDevice((prev) => ({ ...prev, ip_address: e.target.value }))} />
                
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t("port", "Port")}</label>
                    <Input
                  value={selectedDevice.port || ''}
                  onChange={(e) => setSelectedDevice((prev) => ({ ...prev, port: e.target.value }))} />
                
                  </div>
                </div>
            }
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleEditDevice}>{t("saveChanges", "Save Changes")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("deleteDevice", "Delete Device")}</DialogTitle>
            <DialogDescription>{t("areYouSure", "Are you sure you want to delete \"")}
              {selectedDevice?.name}{t("thisActionCannot", "\"? This action cannot be undone.")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>{t("cancel", "Cancel")}</Button>
            <Button variant="destructive" onClick={handleDeleteDevice}>{t("delete", "Delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

// Device Grid Component
const DeviceGrid = ({ devices, loading, testingDevice, onEdit, onDelete, onTest, emptyMessage = "No devices found" }) => {const { t } = useTranslation("wms");
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">{t("loadingDevices", "Loading devices...")}</span>
      </div>);

  }

  if (devices.length === 0) {
    return (
      <div className="text-center py-12">
        <Cpu className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground">{emptyMessage}</p>
      </div>);

  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {devices.map((device) => {
        const TypeIcon = deviceTypeIcons[device.device_type] || Cpu;
        const ConnIcon = connectionTypeIcons[device.connection_type] || Wifi;
        const colors = deviceTypeColors[device.device_type] || deviceTypeColors.barcode_scanner;
        const isTesting = testingDevice === device.id;

        return (
          <Card key={device.id} className="card-elevated hover:shadow-lg transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${colors.bg} ${colors.text} ${colors.dark}`}>
                    <TypeIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{device.name}</h3>
                    <p className="text-xs text-muted-foreground">{device.model || deviceTypeLabels[device.device_type] || device.device_type.replace('_', ' ')}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onTest(device)} className="gap-2">
                      <TestTube className="h-4 w-4" />{t("testConnection", "Test Connection")}

                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onEdit(device)} className="gap-2">
                      <Edit className="h-4 w-4" />{t("edit", "Edit")}

                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onDelete(device)} className="gap-2 text-red-600">
                      <Trash2 className="h-4 w-4" />{t("delete", "Delete")}

                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-3">
                {/* Status */}
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${statusColors[device.status]}`} />
                  <span className="text-sm capitalize">{device.status}</span>
                  {isTesting && <Loader2 className="h-3 w-3 animate-spin ml-1" />}
                </div>

                {/* Connection Info */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ConnIcon className="h-4 w-4" />
                  <span className="capitalize">{device.connection_type?.replace('_', ' ')}</span>
                  {device.ip_address &&
                  <span className="font-mono text-xs">({device.ip_address}:{device.port || 9100})</span>
                  }
                </div>

                {/* Location */}
                {device.location &&
                <p className="text-sm text-muted-foreground truncate">{t("key_3470", "\uD83D\uDCCD")}
                  {device.location}
                  </p>
                }

                {/* Last Seen */}
                {device.last_seen &&
                <p className="text-xs text-muted-foreground">{t("lastSeen", "Last seen:")}
                  {new Date(device.last_seen).toLocaleString()}
                  </p>
                }

                {/* Printer-specific info */}
                {device.device_type === 'label_printer' && device.label_width &&
                <div className="pt-2 border-t border-border flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{t("label", "Label:")}{device.label_width}x{device.label_height}{t("mm", "mm")}</span>
                    <span>{device.dpi}{t("dpi", "DPI")}</span>
                  </div>
                }

                {/* Mobile-specific info */}
                {device.device_type === 'mobile_device' && device.assigned_user &&
                <div className="pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground">{t("assigned", "Assigned:")}{device.assigned_user}</p>
                  </div>
                }

                {/* GPS-specific info */}
                {device.device_type === 'gps_tracker' &&
                <div className="pt-2 border-t border-border">
                    {device.assigned_vehicle && <p className="text-xs text-muted-foreground">{t("vehicle", "Vehicle:")}{device.assigned_vehicle}</p>}
                    {device.last_coordinates &&
                  <p className="text-xs text-muted-foreground font-mono">
                        {device.last_coordinates.lat?.toFixed(4)}, {device.last_coordinates.lng?.toFixed(4)}
                      </p>
                  }
                  </div>
                }

                {/* Temperature sensor-specific info */}
                {device.device_type === 'temperature_sensor' &&
                <div className="pt-2 border-t border-border space-y-1">
                    {device.current_temp !== undefined &&
                  <p className="text-sm font-medium">{t("current", "Current:")}
                    <span className={device.current_temp > device.max_temp || device.current_temp < device.min_temp ? 'text-red-600' : 'text-green-600'}>
                          {device.current_temp}{t("c", "\xB0C")}
                    </span>
                      </p>
                  }
                    {device.min_temp !== undefined && device.max_temp !== undefined &&
                  <p className="text-xs text-muted-foreground">{t("range", "Range:")}{device.min_temp}{t("c", "\xB0C -")}{device.max_temp}{t("c", "\xB0C")}</p>
                  }
                  </div>
                }
              </div>
            </CardContent>
          </Card>);

      })}
    </div>);

};

export default DeviceManagement;